// Date formatting utilities with multiple preset options

// Preset configurations
export const DATE_PRESETS = {
  full: {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  },
  long: {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
  medium: {
    year: "numeric",
    month: "short",
    day: "numeric",
  },
  short: {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  },
  monthYear: {
    year: "numeric",
    month: "long",
  },
  dayMonth: {
    month: "short",
    day: "numeric",
  },
  relative: "relative", // Special flag for relative dates
};

// Timezone options
export const TIMEZONE_PRESETS = {
  utc: "UTC",
  local: undefined, // Uses user's local timezone
  istanbul: "Europe/Istanbul",
  london: "Europe/London",
  newYork: "America/New_York",
};

// Main formatter function
export function formatDate(date, options = {}) {
  const { locale = "en-US", preset = "long", timezone = "UTC", customFormat = null } = options;

  // Input validation
  if (!date) return "";

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    throw new Error(`Invalid date: ${date}`);
  }

  // Handle relative dates
  if (preset === "relative") {
    return formatRelativeDate(dateObj, locale);
  }

  // Use custom format or preset
  const formatOptions = customFormat || DATE_PRESETS[preset] || DATE_PRESETS.long;

  // Add timezone if specified
  if (timezone) {
    formatOptions.timeZone = timezone;
  }

  return dateObj.toLocaleDateString(locale, formatOptions);
}

// Relative date formatting (e.g., "2 days ago", "yesterday")
export function formatRelativeDate(date, locale = "en-US") {
  const now = new Date();
  const dateObj = new Date(date);
  const diffTime = now - dateObj;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Use Intl.RelativeTimeFormat if available
  if (Intl.RelativeTimeFormat) {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

    if (Math.abs(diffDays) < 1) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      if (Math.abs(diffHours) < 1) {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        return rtf.format(-diffMinutes, "minute");
      }
      return rtf.format(-diffHours, "hour");
    } else if (Math.abs(diffDays) < 7) {
      return rtf.format(-diffDays, "day");
    } else if (Math.abs(diffDays) < 30) {
      const diffWeeks = Math.floor(diffDays / 7);
      return rtf.format(-diffWeeks, "week");
    } else if (Math.abs(diffDays) < 365) {
      const diffMonths = Math.floor(diffDays / 30);
      return rtf.format(-diffMonths, "month");
    } else {
      const diffYears = Math.floor(diffDays / 365);
      return rtf.format(-diffYears, "year");
    }
  }

  // Fallback for older browsers
  return formatDate(date, { preset: "medium", locale });
}

// Convenience functions for common use cases
export const formatPublishedAt = (date, locale = "en-US") =>
  formatDate(date, { locale, preset: "long", timezone: "UTC" });

export const formatShortDate = (date, locale = "en-US") => formatDate(date, { locale, preset: "short" });

export const formatFullDate = (date, locale = "en-US") => formatDate(date, { locale, preset: "full" });

export const formatRelative = (date, locale = "en-US") => formatDate(date, { locale, preset: "relative" });

// Date range formatting
export function formatDateRange(startDate, endDate, options = {}) {
  const { locale = "en-US", preset = "medium", timezone = "UTC" } = options;

  const start = formatDate(startDate, { locale, preset, timezone });
  const end = formatDate(endDate, { locale, preset, timezone });

  // Check if same day
  const startObj = new Date(startDate);
  const endObj = new Date(endDate);

  if (startObj.toDateString() === endObj.toDateString()) {
    return start;
  }

  return `${start} - ${end}`;
}

// Time-aware formatting
export function formatDateTime(date, options = {}) {
  const {
    locale = "en-US",
    datePreset = "medium",
    timeFormat = "short",
    timezone = "UTC",
    separator = " at ",
  } = options;

  const dateStr = formatDate(date, { locale, preset: datePreset, timezone });
  const timeStr = new Date(date).toLocaleTimeString(locale, {
    timeStyle: timeFormat,
    timeZone: timezone,
  });

  return `${dateStr}${separator}${timeStr}`;
}

// Utility to check if date is valid
export function isValidDate(date) {
  const dateObj = new Date(date);
  return !isNaN(dateObj.getTime());
}

// Get available locales (for configuration UI)
export function getSupportedLocales() {
  return [
    { code: "en-US", name: "English (US)" },
    { code: "en-GB", name: "English (UK)" },
    { code: "tr-TR", name: "Türkçe" },
    { code: "de-DE", name: "Deutsch" },
    { code: "fr-FR", name: "Français" },
    { code: "es-ES", name: "Español" },
    { code: "it-IT", name: "Italiano" },
    { code: "pt-BR", name: "Português (Brasil)" },
    { code: "ru-RU", name: "Русский" },
    { code: "ja-JP", name: "日本語" },
    { code: "zh-CN", name: "中文 (简体)" },
  ];
}
